import { ArrowLeft, Shield, Database, Globe, Lock, Bell, UserCheck, Trash2, Mail } from "lucide-react";
import { Helmet } from "react-helmet";
import { Card, CardContent } from "@/components/ui/card";
import ThemeToggle from "@/components/layout/ThemeToggle";

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen flex flex-col items-center p-4 pb-12 bg-gradient-to-br from-sky-50 via-indigo-50 to-blue-50 dark:from-slate-950 dark:via-indigo-950 dark:to-blue-950 bg-fixed">
      <Helmet>
        <title>Privacy Policy | Dynamatix Intranet</title>
      </Helmet>

      <div className="absolute inset-0 bg-grid-slate-100 [mask-image:linear-gradient(0deg,rgba(255,255,255,0.5),rgba(255,255,255,0.8))] dark:[mask-image:linear-gradient(0deg,rgba(0,0,0,0.3),rgba(0,0,0,0.5))] -z-10" />

      <div className="absolute top-4 right-4 z-10">
        <ThemeToggle />
      </div>

      <div className="absolute top-4 left-4 z-10">
        <a
          href="/login"
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Login
        </a>
      </div>

      {/* Logo */}
      <div className="flex items-center gap-3 mt-12 mb-8">
        <img src="/logo/singleLogo.png" alt="Dynamatix" className="h-10 w-10" />
        <div className="text-2xl font-bold text-foreground">Dynamatix</div>
      </div>

      <Card className="w-full max-w-3xl shadow-xl border-0">
        <CardContent className="p-6 md:p-10">
          {/* Title */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-primary/10 mb-4">
              <Shield className="h-6 w-6 text-primary" />
            </div>
            <h1 className="text-2xl md:text-3xl font-bold text-foreground">Privacy Policy</h1>
            <p className="text-sm text-muted-foreground mt-2">
              Last updated: February 19, 2026
            </p>
          </div>

          <div className="space-y-8 text-sm text-muted-foreground leading-relaxed">
            {/* Introduction */}
            <section>
              <p>
                Dynamatix, Inc. ("Dynamatix", "we", "us", or "our") is committed to protecting the privacy of our
                users. This Privacy Policy explains how we collect, use, store, and protect information when you use the
                Dynamatix Intranet web application and the Dynamatix Intranet Chrome Extension (collectively, the "Services").
              </p>
              <p className="mt-3">
                By using our Services, you agree to the collection and use of information as described in this policy. Our Services
                are intended for use by authorized employees and contractors of organizations that have subscribed to
                the Dynamatix Intranet platform.
              </p>
            </section>

            {/* Section 1 */}
            <section>
              <div className="flex items-center gap-2 mb-3">
                <Database className="h-4 w-4 text-primary flex-shrink-0" />
                <h2 className="text-base font-semibold text-foreground">1. Information We Collect</h2>
              </div>

              <h3 className="font-medium text-foreground mt-4 mb-1">a) Account Information</h3>
              <p>
                When your organization creates your account, we store your name, email address, job title,
                department, and role within the platform. This information is provided by your organization's
                administrator.
              </p>

              <h3 className="font-medium text-foreground mt-4 mb-1">b) Authentication Data</h3>
              <p>
                We support sign-in via email/password and third-party OAuth providers (Google and Microsoft). When you
                authenticate via Google or Microsoft, we receive your name, email address, and profile picture URL from
                the provider. We do not store your Google or Microsoft password. Authentication tokens (JWTs) are stored
                locally on your device to maintain your session.
              </p>

              <h3 className="font-medium text-foreground mt-4 mb-1">c) Usage Data</h3>
              <p>
                We collect data generated through your use of the Services, including: task effort entries (hours,
                descriptions, dates, work location), timer sessions, task assignments and updates, project activity,
                leave requests, timesheet entries, and survey responses.
              </p>

              <h3 className="font-medium text-foreground mt-4 mb-1">d) Chrome Extension Data</h3>
              <p>
                The Dynamatix Intranet Chrome Extension stores the following data locally on your device using
                Chrome's <code className="bg-muted px-1 py-0.5 rounded text-xs">chrome.storage</code> API:
              </p>
              <ul className="list-disc list-inside mt-2 space-y-1 ml-2">
                <li>Authentication token (for API communication)</li>
                <li>User profile information (name, email, role)</li>
                <li>Timer state (running/stopped, elapsed time, selected task)</li>
                <li>User preferences (default work location)</li>
              </ul>
              <p className="mt-2">
                This data is stored entirely on your local device and is not transmitted to any third party.
              </p>
            </section>

            {/* Section 2 */}
            <section>
              <div className="flex items-center gap-2 mb-3">
                <Globe className="h-4 w-4 text-primary flex-shrink-0" />
                <h2 className="text-base font-semibold text-foreground">2. How We Use Your Information</h2>
              </div>
              <p>We use the information we collect to:</p>
              <ul className="list-disc list-inside mt-2 space-y-1 ml-2">
                <li>Authenticate you and maintain your session across the web application and Chrome Extension</li>
                <li>Provide core functionality including task management, time tracking, effort logging, and reporting</li>
                <li>Display personalized dashboards, analytics, and notifications</li>
                <li>Enable team collaboration features such as task assignments, project management, and leave approvals</li>
                <li>Generate organizational reports and analytics for authorized managers and administrators</li>
                <li>Send notifications about task updates, approvals, and system events</li>
              </ul>
            </section>

            {/* Section 3 */}
            <section>
              <div className="flex items-center gap-2 mb-3">
                <Lock className="h-4 w-4 text-primary flex-shrink-0" />
                <h2 className="text-base font-semibold text-foreground">3. Data Storage & Security</h2>
              </div>
              <p>
                All data transmitted between your browser (or the Chrome Extension) and our servers is encrypted using
                HTTPS/TLS. Our servers are hosted on secure cloud infrastructure with industry-standard protections
                including encryption at rest, network isolation, and access controls.
              </p>
              <p className="mt-3">
                Authentication tokens are short-lived JWTs. The Chrome Extension communicates exclusively with
                <code className="bg-muted px-1 py-0.5 rounded text-xs ml-1">https://intranet.dynamatix.com</code> and
                does not send data to any other domain.
              </p>
            </section>

            {/* Section 4 */}
            <section>
              <div className="flex items-center gap-2 mb-3">
                <UserCheck className="h-4 w-4 text-primary flex-shrink-0" />
                <h2 className="text-base font-semibold text-foreground">4. Data Sharing</h2>
              </div>
              <p>
                We do not sell, rent, or trade your personal information to third parties. Your data is shared only in the following circumstances:
              </p>
              <ul className="list-disc list-inside mt-2 space-y-1 ml-2">
                <li>
                  <span className="font-medium text-foreground">Within your organization:</span> Managers and
                  administrators within your organization can view effort data, task assignments, and reports relevant to their teams.
                </li>
                <li>
                  <span className="font-medium text-foreground">OAuth providers:</span> When you sign in with Google or
                  Microsoft, we exchange authentication tokens with the provider. We only receive basic profile
                  information (name, email, profile picture).
                </li>
                <li>
                  <span className="font-medium text-foreground">Legal requirements:</span> We may disclose information
                  if required by law, regulation, or legal process.
                </li>
              </ul>
            </section>

            {/* Section 5 */}
            <section>
              <div className="flex items-center gap-2 mb-3">
                <Bell className="h-4 w-4 text-primary flex-shrink-0" />
                <h2 className="text-base font-semibold text-foreground">5. Chrome Extension Permissions</h2>
              </div>
              <p>The Dynamatix Intranet Chrome Extension requests the following permissions:</p>
              <div className="mt-3 space-y-3">
                <div className="flex gap-3 p-3 rounded-lg bg-muted/50">
                  <code className="text-xs font-semibold text-primary whitespace-nowrap">storage</code>
                  <span>Stores your authentication token, timer state, and preferences locally on your device.</span>
                </div>
                <div className="flex gap-3 p-3 rounded-lg bg-muted/50">
                  <code className="text-xs font-semibold text-primary whitespace-nowrap">alarms</code>
                  <span>Keeps the task timer running accurately in the background when the extension popup is closed.</span>
                </div>
                <div className="flex gap-3 p-3 rounded-lg bg-muted/50">
                  <code className="text-xs font-semibold text-primary whitespace-nowrap">notifications</code>
                  <span>Displays browser notifications for timer completion and effort logging confirmations.</span>
                </div>
                <div className="flex gap-3 p-3 rounded-lg bg-muted/50">
                  <code className="text-xs font-semibold text-primary whitespace-nowrap">identity</code>
                  <span>Enables secure Google OAuth sign-in directly from the extension.</span>
                </div>
                <div className="flex gap-3 p-3 rounded-lg bg-muted/50">
                  <code className="text-xs font-semibold text-primary whitespace-nowrap">host_permissions</code>
                  <span>
                    Allows API requests to <code className="text-xs">intranet.dynamatix.com</code> only. No other
                    domains are accessed.
                  </span>
                </div>
              </div>
            </section>

            {/* Section 6 */}
            <section>
              <div className="flex items-center gap-2 mb-3">
                <Trash2 className="h-4 w-4 text-primary flex-shrink-0" />
                <h2 className="text-base font-semibold text-foreground">6. Data Retention & Deletion</h2>
              </div>
              <p>
                Your account data is retained for as long as your organization maintains an active subscription to the
                Dynamatix Intranet platform. When your account is deactivated by your organization's administrator, your
                personal data is de-identified or deleted in accordance with our data retention schedule.
              </p>
              <p className="mt-3">
                Chrome Extension local data (auth tokens, timer state, preferences) can be cleared at any time by
                logging out of the extension or uninstalling it. No residual data is retained after uninstallation.
              </p>
            </section>

            {/* Section 7 */}
            <section>
              <div className="flex items-center gap-2 mb-3">
                <Shield className="h-4 w-4 text-primary flex-shrink-0" />
                <h2 className="text-base font-semibold text-foreground">7. Your Rights</h2>
              </div>
              <p>Depending on your jurisdiction, you may have the right to:</p>
              <ul className="list-disc list-inside mt-2 space-y-1 ml-2">
                <li>Access the personal data we hold about you</li>
                <li>Request correction of inaccurate data</li>
                <li>Request deletion of your data (subject to organizational and legal requirements)</li>
                <li>Export your data in a portable format</li>
              </ul>
              <p className="mt-3">
                To exercise any of these rights, please contact your organization's administrator or reach out to us
                directly using the contact information below.
              </p>
            </section>

            {/* Section 8 */}
            <section>
              <div className="flex items-center gap-2 mb-3">
                <Globe className="h-4 w-4 text-primary flex-shrink-0" />
                <h2 className="text-base font-semibold text-foreground">8. Cookies & Tracking</h2>
              </div>
              <p>
                The Dynamatix Intranet web application uses essential cookies and local storage to maintain your
                authentication session and preferences. We do not use third-party advertising cookies or cross-site
                tracking technologies. Error monitoring is performed via Sentry for application stability purposes only.
              </p>
            </section>

            {/* Section 9 */}
            <section>
              <div className="flex items-center gap-2 mb-3">
                <Lock className="h-4 w-4 text-primary flex-shrink-0" />
                <h2 className="text-base font-semibold text-foreground">9. Changes to This Policy</h2>
              </div>
              <p>
                We may update this Privacy Policy from time to time. When we make changes, we will update the "Last
                updated" date at the top of this page. We encourage you to review this policy periodically for any
                updates. Continued use of the Services after changes constitutes acceptance of the revised policy.
              </p>
            </section>

            {/* Section 10 */}
            <section>
              <div className="flex items-center gap-2 mb-3">
                <Mail className="h-4 w-4 text-primary flex-shrink-0" />
                <h2 className="text-base font-semibold text-foreground">10. Contact Us</h2>
              </div>
              <p>If you have any questions or concerns about this Privacy Policy, please contact us:</p>
              <div className="mt-3 p-4 rounded-lg bg-muted/50 space-y-1">
                <p className="font-medium text-foreground">Dynamatix, Inc.</p>
                <p>Email: <a href="mailto:privacy@dynamatix.com" className="text-primary hover:underline">privacy@dynamatix.com</a></p>
                <p>Web: <a href="https://intranet.dynamatix.com" className="text-primary hover:underline">intranet.dynamatix.com</a></p>
              </div>
            </section>
          </div>
        </CardContent>
      </Card>

      <footer className="mt-10 text-center text-sm text-muted-foreground">
        <p>&copy; {new Date().getFullYear()} Dynamatix, Inc. All rights reserved.</p>
        <p className="mt-1">Secure enterprise intranet portal</p>
      </footer>
    </div>
  );
}
